/**
 * The year-by-year table — the signature element (spec §18/§21):
 * every row, sortable, today's-dollars aware, CSV-exportable from the parent.
 */
import { useMemo, useState } from 'react';
import type { YearSnapshot } from '../engine/snapshot';
import { fmtUsd } from './format';

interface Props {
  snapshots: YearSnapshot[];
  todaysDollars: boolean;
}

interface Col {
  key: string;
  label: string;
  get: (s: YearSnapshot) => number;
  money?: boolean;
}

export function YearTable({ snapshots, todaysDollars }: Props) {
  const cols: Col[] = useMemo(
    () => [
      { key: 'year', label: 'Year', get: (s) => s.year },
      { key: 'age', label: 'Age', get: (s) => Object.values(s.ages)[0] ?? 0 },
      { key: 'income', label: 'Work income', money: true,
        get: (s) => s.wagesIncome + s.selfEmploymentIncome },
      { key: 'ss', label: 'Social Security', money: true, get: (s) => s.socialSecurityGross },
      { key: 'pension', label: 'Pension + annuity', money: true,
        get: (s) => s.pensionIncome + s.annuityIncome },
      { key: 'rmd', label: 'RMD', money: true, get: (s) => s.totalRmd },
      { key: 'spend', label: 'Spending', money: true, get: (s) => s.totalSpending },
      { key: 'outflow', label: 'Total outflow', money: true, get: (s) => s.totalOutflow },
      { key: 'wd', label: 'Withdrawals', money: true, get: (s) => s.totalWithdrawals },
      { key: 'tax', label: 'Tax', money: true, get: (s) => s.totalTaxLiability },
      { key: 'marg', label: 'Marginal', get: (s) => s.federal.marginalOrdinaryRate },
      { key: 'pretax', label: 'Pre-tax', money: true, get: (s) => s.balancesByFamily.pretax },
      { key: 'roth', label: 'Roth', money: true, get: (s) => s.balancesByFamily.roth },
      { key: 'aftertax', label: 'After-tax', money: true, get: (s) => s.balancesByFamily.aftertax },
      { key: 'nw', label: 'Net worth', money: true, get: (s) => s.netWorth },
    ],
    [],
  );

  const [sortKey, setSortKey] = useState('year');
  const [sortDir, setSortDir] = useState<1 | -1>(1);

  const sorted = useMemo(() => {
    const col = cols.find((c) => c.key === sortKey) ?? cols[0]!;
    return [...snapshots].sort((a, b) => (col.get(a) - col.get(b)) * sortDir);
  }, [snapshots, cols, sortKey, sortDir]);

  function onSort(key: string) {
    if (key === sortKey) setSortDir((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(key);
      setSortDir(1);
    }
  }

  function cell(col: Col, s: YearSnapshot): string {
    const raw = col.get(s);
    if (col.key === 'marg') return `${Math.round(raw * 100)}%`;
    if (!col.money) return String(raw);
    const adj = todaysDollars ? raw / s.cumulativeInflationFactor : raw;
    return fmtUsd(adj);
  }

  return (
    <div className="table-wrap">
      <table className="years">
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c.key} onClick={() => onSort(c.key)} scope="col"
                  aria-sort={sortKey === c.key ? (sortDir === 1 ? 'ascending' : 'descending') : 'none'}>
                {c.label}
                {sortKey === c.key ? (sortDir === 1 ? ' ↑' : ' ↓') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((s) => (
            <tr key={s.year} className={s.depleted ? 'depleted' : undefined}>
              {cols.map((c) => (
                <td key={c.key} className={c.money && c.get(s) < 0 ? 'neg' : undefined}>
                  {cell(c, s)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
